import { State } from '@app/store/state';
import { memoize } from 'proxy-memoize';

import { isSameEventPart } from './routes/recordings/components/share-menu/constants';

export const selectContentPages = memoize((state: State) => {
  if (state.contentHub.pageInfo.pages.status === 'success')
    return {
      status: state.contentHub.pageInfo.pages.status,
      data: state.contentHub.pageInfo.pages.data[
        state.contentHub.pageInfo.pages.pagination.page
      ],
      pagination: state.contentHub.pageInfo.pages.pagination,
    };

  return {
    status: state.contentHub.pageInfo.pages.status,
    data: null,
  };
});

export const selectPageDeletionInfo = (state: State) =>
  state.contentHub.pageInfo.pageDeletionInfo;

export const selectPageSearchText = (state: State) =>
  state.contentHub.pageInfo.searchText;

export const selectLibrarySearchText = (state: State) =>
  state.contentHub.libraryInfo.filters.searchText;

export const selectLibraryFilters = (state: State) =>
  state.contentHub.libraryInfo.filters;

export const selectCurrentPage = (state: State) =>
  state.contentHub.pageInfo.currentPage;

export const selectContentHubAuthStatus = (state: State) =>
  state.contentHub.authentication.status;

export const selectContentHubPageStatus = (state: State) =>
  state.contentHub.pageInfo.pages.status;

export const selectContentHubTotalPageCount = (state: State) => {
  return state.contentHub.pageInfo.pages.pagination?.totalRecords ?? 0;
};

export const selectLibraryPageCursor = (state: State) =>
  state.contentHub.libraryInfo.recordings?.pagination?.next_cursor;

export const selectLibraryPageStatus = (state: State) =>
  state.contentHub.libraryInfo.recordings?.status;

export const selectLibraryRecordings = (state: State) =>
  state.contentHub.libraryInfo.recordings?.data;

export const selectIsLibraryLoaded = (state: State) =>
  state.contentHub.libraryInfo.isLibraryLoaded;

export const selectHasActiveLibraryFilters = (state: State) => {
  const filters = state.contentHub.libraryInfo.filters;

  return (
    filters.searchText !== '' ||
    filters.videoType !== 'all' ||
    filters.eventAreas.length > 0 ||
    filters.dateFilter !== 'all' ||
    (filters.events.selectedEvent && filters.events.selectedEvent.value !== '')
  );
};

export const selectRecordingDeletionInfo = (state: State) =>
  state.contentHub.libraryInfo.recordingDeletionInfo;

export const selectRecordingRenameInfo = (state: State) =>
  state.contentHub.libraryInfo.recordingRenameInfo;

export const selectIsPagesLoaded = (state: State) =>
  state.contentHub.pageInfo.isPagesLoaded;

export const selectCreatorLabSelectedTab = (state: State) =>
  state.contentHub.creatorLab.selectedContent?.type ?? 'social-media';

export const selectContentHubConfig = (state: State) => state.contentHub.config;

export const selectPublishToEventsEventsList = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.eventsList;

export const selectPublishToEventsPageInfo = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.pageInfo;

export const selectPublishToEventsStatus = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.status;

export const selectPublishToEventsSearchText = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.searchText;

/**
 * The current in-progress selection state for the publish-to-events flow.
 * This can diverge from initialSelectedEvents while the user is making changes.
 * initialSelectedEvents comes from the GET /event_assignments endpoint request when the flow opens.
 */
export const selectPublishToEventsDraftSelectedEvents = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.draftSelectedEvents;

export const selectPublishToEventsInitialSelectedEvents = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.initialSelectedEvents;

export const selectInitialPublishedEventsCount = memoize(
  (state: State) =>
    Object.keys(selectPublishToEventsInitialSelectedEvents(state)).length,
);

export const selectIsPublishToEventsDirty = memoize((state: State) => {
  const draftSelectedEvents = selectPublishToEventsDraftSelectedEvents(state);
  const initialSelectedEvents =
    selectPublishToEventsInitialSelectedEvents(state);

  const draftEventIds = Object.keys(draftSelectedEvents);
  const initialEventIds = Object.keys(initialSelectedEvents);

  if (draftEventIds.length !== initialEventIds.length) {
    return true;
  }

  return draftEventIds.some(eventId => {
    const initialEventPart = initialSelectedEvents[eventId];
    const draftEventPart = draftSelectedEvents[eventId];

    return (
      !initialEventPart ||
      !draftEventPart ||
      !isSameEventPart(initialEventPart, draftEventPart)
    );
  });
});

export const selectPublishToEventsAssignmentsStatus = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.assignmentsStatus;

export const selectPublishToEventsVideoAreasByEventId = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.videoAreas.byEventId;

export const selectPublishToEventsEventAreaValidationErrors = (state: State) =>
  state.contentHub.recordingInfo.publishToEvents.eventAreaValidationErrors;

export const selectHasPublishToEventsPendingAreaResolution = memoize(
  (state: State) => {
    const draftSelectedEvents = selectPublishToEventsDraftSelectedEvents(state);
    const videoAreasByEventId = selectPublishToEventsVideoAreasByEventId(state);

    return Object.entries(draftSelectedEvents).some(
      ([eventId, eventPart]) =>
        eventPart === null &&
        videoAreasByEventId[eventId]?.status !== 'success',
    );
  },
);

export const selectRecentContentStatus = (state: State) =>
  state.contentHub.libraryInfo.recentContent.status;

export const selectRecentContent = (state: State) =>
  state.contentHub.libraryInfo.recentContent;
